import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable, StatusBadge, fmt } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const METHODS = ["Efectivo", "Transferencia", "Depósito", "Tarjeta", "Cheque", "Pago online"];

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ invoice_id: "", amount: "", method: "Efectivo", reference: "" });
  const { user } = useAuth();
  const canApprove = ["superadmin", "admin", "contabilidad"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/payments").then((r) => setPayments(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    api.get("/invoices?status=pendiente").then((r) => setPendingInvoices(r.data)).catch(() => {});
    setOpen(true);
  };

  const submit = async () => {
    try {
      const inv = pendingInvoices.find((i) => i.id === form.invoice_id);
      await api.post("/payments", { ...form, amount: parseFloat(form.amount) || inv?.total });
      toast.success("Pago registrado como PAGADO");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const act = async (p, action) => {
    try {
      await api.post(`/payments/${p.id}/${action}`);
      toast.success(action === "approve" ? "Pago marcado como PAGADO" : "Pago rechazado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const columns = [
    { key: "invoice_number", label: "Factura", render: (p) => <span className="font-mono text-xs">{p.invoice_number}</span> },
    { key: "student_name", label: "Estudiante" },
    { key: "amount", label: "Monto", render: (p) => <span className="font-semibold">{fmt(p.amount)}</span> },
    { key: "method", label: "Método" },
    { key: "reference", label: "Referencia", render: (p) => <span className="font-mono text-xs">{p.reference || "—"}</span> },
    { key: "created_by", label: "Registrado por" },
    { key: "status", label: "Estado", render: (p) => <StatusBadge value={p.status} /> },
    {
      key: "_a", label: "Acciones",
      render: (p) => canApprove && p.status === "en_revision" ? (
        <div className="flex gap-1">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" data-testid={`approve-payment-${p.id}`} onClick={() => act(p, "approve")}>
            <Check className="w-4 h-4 mr-1" /> Aprobar
          </Button>
          <Button size="sm" variant="outline" className="text-rose-600" data-testid={`reject-payment-${p.id}`} onClick={() => act(p, "reject")}>
            <X className="w-4 h-4 mr-1" /> Rechazar
          </Button>
        </div>
      ) : null,
    },
  ];

  return (
    <div data-testid="payments-page">
      <PageHeader title="Pagos" subtitle="Registro y aprobación de pagos">
        <Button data-testid="create-payment-button" className="bg-blue-800 hover:bg-blue-700" onClick={openCreate}>
          <Plus className="w-4 h-4 mr-1" /> Registrar pago
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={payments} testid="payments-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar pago</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Factura pendiente</Label>
              <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v })}>
                <SelectTrigger data-testid="payment-invoice-select"><SelectValue placeholder="Seleccionar factura" /></SelectTrigger>
                <SelectContent>
                  {pendingInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.number} — {i.student_name} ({fmt(i.total)})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Monto (vacío = total de la factura)</Label><Input type="number" data-testid="payment-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Método</Label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Referencia</Label><Input data-testid="payment-reference" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-payment" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-payment" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>Registrar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
